import * as React from "react";
import { FC } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { Link } from "react-router-dom";
import Markdown from "react-markdown";
import { MainLayoutContainer } from "components/Layout";
import { DragNDropFile } from "components/DragNDropFile";
import CustomTable from "components/Table/CustomTable";
import { MarkdownStyleContainer } from "components/Container/MarkdownStyleContainer";
import { AdminRoutes } from "config/routes";
import { UPLOAD_FILE_MODAL } from "helpers/constants";
import { ReportAlert } from "../UploadCSVUsers/components";
import { useUploadCSVCohorts } from "./useUploadCSVCohorts";

export const UploadCSVCohorts: FC = () => {
  const {
    loading,
    completed,
    displayData,
    data,
    file,
    error,
    loadingData,
    report,
    selectedData,
    setDisplayData,
    setFile,
    handleContinue,
    handleDownloadClick,
    onSubmit,
    handleOnComplete,
  } = useUploadCSVCohorts();

  return (
    <MainLayoutContainer title="Upload CSV Cohorts" maxWidth={960}>
      <Box>
        {error && <Alert severity="error">{error}</Alert>}

        {!displayData && !loading && !completed && (
          <Box>
            <Box mt={2}>
              <DragNDropFile
                maxFileSizeMB={1}
                allowedExtensions={["csv"]}
                onFileUploaded={(uploadedFile) => setFile(uploadedFile)}
              />
            </Box>
            <Box mt={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography component={"div"} variant="h6">
                  CSV Format Required
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleDownloadClick}
                  startIcon={<FileDownload />}
                  size="small"
                >
                  Download Template
                </Button>
              </Stack>
              <Box
                px={2}
                py={1}
                mt={1}
                sx={{
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  border: "1px solid rgba(25, 118, 210, 0.3)",
                  borderRadius: 1,
                }}
              >
                <MarkdownStyleContainer>
                  <Markdown>
                    {UPLOAD_FILE_MODAL.TEMPLATE_EXAMPLE_DESCRIPTION_COHORTS}
                  </Markdown>
                </MarkdownStyleContainer>
              </Box>
            </Box>
          </Box>
        )}

        {displayData && !loading && !completed && (
          <Box mt={2}>
            <CustomTable
              entities={data}
              columns={[
                { id: "name", label: "Name", value: "name" },
                { id: "program_id", label: "Program ID", value: "program_id" },
                { id: "start_date", label: "Start Date", value: "start_date" },
                { id: "academic_year", label: "Academic Year", value: "academic_year" },
              ]}
              handleActionClick={() => {}}
              fieldToFilter={"name"}
              isSelectable={true}
              extraActionItems={[]}
              idColumn={"row_key"}
              entitiesIdsAlreadySelected={selectedData.current}
              onSelectionChanged={(selected) => {
                selectedData.current = selected;
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
          {!displayData && !loading && !completed && (
            <>
              <Box mr={2}>
                <Button variant="outlined" component={Link} to={AdminRoutes.Home}>
                  Cancel
                </Button>
              </Box>
              <Box mr={2}>
                <Button
                  disabled={!file}
                  onClick={() => handleContinue(file as File)}
                  variant="contained"
                  color="primary"
                >
                  Continue
                </Button>
              </Box>
            </>
          )}
          {displayData && !loading && !completed && (
            <>
              <Box mr={2}>
                <Button variant="outlined" onClick={() => setDisplayData(false)}>
                  Back
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  onClick={onSubmit}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
            </>
          )}
        </Box>

        {loading && (
          <Box mt={2} mb={4} maxWidth={720} sx={{ mx: "auto" }}>
            <Typography variant="body1" align="center">
              Uploading {~~((loadingData.current / loadingData.total) * 100)} %
            </Typography>
            <Box sx={{ width: "100%" }}>
              <LinearProgress
                variant="determinate"
                value={(loadingData.current / loadingData.total) * 100}
              />
            </Box>
          </Box>
        )}

        {completed && (
          <Box>
            <Stack spacing={2}>
              {report.success.length > 0 && (
                <ReportAlert
                  report={report.success}
                  severity="success"
                  textDescription="Cohorts imported successfully"
                />
              )}
              {report.alreadyExists.length > 0 && (
                <ReportAlert
                  report={report.alreadyExists}
                  severity="warning"
                  textDescription="Cohorts already existed"
                />
              )}
              {report.error.length > 0 && (
                <ReportAlert
                  report={report.error}
                  severity="error"
                  textDescription="Cohorts failed to import"
                />
              )}
            </Stack>
            <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Button
                disabled={loading}
                onClick={handleOnComplete}
                type="submit"
                variant="contained"
                color="primary"
              >
                Complete
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </MainLayoutContainer>
  );
};
